import { useCallback, useEffect, useState } from 'react';
import { useLocaleStore } from '@/lib/core/stores';
import { getRelatedProducts } from './catalog';
import type { ProductListItemDto } from '@/services/platform/api/model';

export interface UseRelatedProductsResult {
  data: ProductListItemDto[];
  isLoading: boolean;
}

/**
 * Fetches related products from the dedicated catalog endpoint
 * (`/catalog/products/{guid}/related`). The server ranks by category +
 * gender, preferring products tagged "New" in Shopify, and excludes the
 * source product — no fallback, so the list can be empty. Returns an empty
 * list while the product GUID is unknown.
 */
export function useRelatedProducts(productGuid?: string, limit = 6): UseRelatedProductsResult {
  const locale = useLocaleStore(state => state.locale);
  const [data, setData] = useState<ProductListItemDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const load = useCallback(async () => {
    if (!productGuid) {
      setData([]);
      return;
    }
    setIsLoading(true);
    try {
      const items = await getRelatedProducts(productGuid, limit, locale);
      setData(items ?? []);
    } finally {
      setIsLoading(false);
    }
  }, [productGuid, limit, locale]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, isLoading };
}
